<template>
  <div v-if="notFound" class="py-6">
    <div class="lg:px-12 px-4 sm:px-6 md:px-8">
      <h1 class="text-3xl text-gray-700 font-bold mt-8 mb-3 text-center">
        Not Found
      </h1>
      <p class="text-center">The page you requested was not found.</p>
      <p class="text-center">Try going <a class="text-purple-600" href="/">Home</a> or check the URL and try again.</p>
    </div>
  </div>
  <div v-else class="h-screen flex overflow-hidden bg-white">
    <TransitionRoot as="template" :show="sidebarOpen">
      <Dialog
        as="div"
        static
        class="fixed inset-0 flex z-40 md:hidden"
        @close="sidebarOpen = false"
        :open="sidebarOpen"
      >
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </TransitionChild>
        <TransitionChild
          as="template"
          enter="transition ease-in-out duration-300 transform"
          enter-from="-translate-x-full"
          enter-to="translate-x-0"
          leave="transition ease-in-out duration-300 transform"
          leave-from="translate-x-0"
          leave-to="-translate-x-full"
        >
          <div
            class="
              relative
              flex-1 flex flex-col
              max-w-xs
              w-full
              pt-5
              pb-4
              bg-white
            "
          >
            <TransitionChild
              as="template"
              enter="ease-in-out duration-300"
              enter-from="opacity-0"
              enter-to="opacity-100"
              leave="ease-in-out duration-300"
              leave-from="opacity-100"
              leave-to="opacity-0"
            >
              <div class="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  class="
                    ml-1
                    flex
                    items-center
                    justify-center
                    h-10
                    w-10
                    rounded-full
                    focus:outline-none
                    focus:ring-2 focus:ring-inset focus:ring-white
                  "
                  @click="sidebarOpen = false"
                >
                  <span class="sr-only">Close sidebar</span>
                  <XIcon class="h-6 w-6 text-white" aria-hidden="true" />
                </button>
              </div>
            </TransitionChild>
            <div class="flex-shrink-0 flex items-center font-bold text-lg text-blue-700 px-5">
              {{ firmName }}
            </div>
            <div class="flex items-center uppercase font-bold text-xs text-gray-400 px-6">
              {{ role }}
            </div>
            <div class="mt-5 flex-1 h-0 overflow-y-auto">
              <nav class="flex-1 px-2 bg-white space-y-1">
                <div v-for="item in navigation" :key="item.name">
                  <router-link
                    v-if="item.href != 'None'"
                    @click="sidebarOpen = false; changeMenu(navigation.indexOf(item))"
                    :to="`/firm/${firmId}${slash(item.name)}${item.href}`"
                    :class="[current == item.href ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900', 'group flex items-center px-2 py-2 text-base font-medium rounded-md']"
                  >
                    <component
                      :is="item.icon"
                      :class="[current == item.href ? 'text-gray-500' : 'text-gray-400 group-hover:text-gray-500', 'mr-4 h-6 w-6']"
                      aria-hidden="true"
                    />
                    {{ item.name }}
                    <Spinner v-if="item.name === 'Email' && emailProcessingStatus === 'started'" class="inline ml-2" mode="light" :size="4" />
                  </router-link>
                  <div v-else class="relative group" :class="[item.showSubMenus ? 'bg-gray-200 rounded-md pb-2' : '']">
                    <button
                      @click="item.showSubMenus = !item.showSubMenus"
                      :class="[
                        'w-full text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                        'group flex items-center px-2 py-2 text-base font-medium rounded-md'
                      ]"
                    >
                      <component
                        :is="item.icon"
                        :class="['text-gray-400 group-hover:text-gray-500', 'mr-4 h-6 w-6']"
                        aria-hidden="true"
                      />
                      {{ item.name }}
                      <span class="ml-auto flex items-center">
                        <ChevronDownIcon
                          v-if="item.showSubMenus"
                          class="h-5 w-5 transition-transform"
                        />
                        <ChevronRightIcon
                          v-else
                          class="h-5 w-5 transition-transform"
                        />
                      </span>
                    </button>
                    <div v-show="item.showSubMenus" class="space-y-1 pl-10">
                      <router-link
                        v-for="subItem in item.subMenus"
                        @click="sidebarOpen = false; changeMenu(navigation.indexOf(item), item.subMenus.indexOf(subItem))"
                        :key="subItem.name"
                        :to="`/firm/${firmId}${slash(subItem.name)}${subItem.href}`"
                        :class="[
                          current == subItem.href ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                          'm-2 group flex items-center py-1 text-sm font-semibold rounded-md']"
                      >
                        {{ subItem.name }}
                      </router-link>
                    </div>
                  </div>
                </div>
              </nav>
            </div>
          </div>
        </TransitionChild>
        <div class="flex-shrink-0 w-14" aria-hidden="true">
          <!-- Dummy element to force sidebar to shrink to fit close icon -->
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- sidebar for desktop -->
    <div class="hidden md:flex md:flex-shrink-0" v-if="desktopMenuOpen">
      <div class="w-64 flex">
        <!-- Sidebar component, swap this element with another sidebar if you like -->
        <div
          class="
            flex-col flex-grow
            border-r
            pt-5
            pb-4
            bg-custom-purple-900
            text-white
            overflow-y-auto
          "
        >
          <div class="flex items-center uppercase font-semibold text-lg px-5">
            {{ firmName }}
          </div>
          <div class="flex items-center uppercase font-semibold text-xs px-6">
            {{ role }}
          </div>
          <EmailProcessor userId="userId" firmId="userId"/>
          <div class="absolute top-6 left-50">
            <ChevronLeftIcon  class="h-8 w-8 text-white font-bold cursor-pointer bg-custom-purple-500 rounded-full hover:bg-custom-purple-300
            absolute top-6 -translate-x-1/2 left-64 p-1 shadow-sm z-40"
              @click="desktopMenuOpen = false"
            />
          </div>
          <div class="mt-5 flex-grow flex flex-col">
            <nav class="flex-1 px-2 space-y-1 h-screen">
              <div v-for="(item, index) in navigation" :key="item.name">
                <router-link
                  v-if="item.href != 'None'"
                  :to="`/firm/${firmId}${slash(item.name)}${item.href}`"
                  :class="[
                    (item.name === 'Settings' && windowHeight > 585) ? 'absolute bottom-20 w-60'  : '',
                    (item.name === 'Help' && windowHeight > 585) ? 'absolute bottom-6 w-60' : '',
                    item.current ? 'bg-custom-purple-300' : 'hover:bg-custom-purple-300', 'group flex items-center text-base rounded-md', 'px-2 py-2'
                  ]"
                  @click="changeMenu(index)"
                >
                  <component
                    :is="item.icon"
                    class="bg-custom-purple-500 mr-4 h-10 w-10 p-2 rounded-md"
                    aria-hidden="true"
                  />
                  {{ item.name }}
                  <Spinner v-if="item.name === 'Email' && emailProcessingStatus === 'started'" class="inline ml-2" mode="light" :size="4" />
                </router-link>
                <div v-else class="relative group" :class="[item.showSubMenus ? 'bg-custom-purple-300 rounded-md pb-2' : '']">
                  <button
                    @click="changeMenu(index)"
                    :class="[
                      'w-full text-white hover:bg-custom-purple-300',
                      'group flex items-center px-2 py-2 text-base rounded-md'
                    ]"
                  >
                    <component
                      :is="item.icon"
                      :class="['text-white bg-custom-purple-500', 'mr-4 h-10 w-10 p-2 rounded-md']"
                      aria-hidden="true"
                    />
                    {{ item.name }}
                    <span class="ml-auto flex items-center">
                      <ChevronDownIcon
                        v-if="item.showSubMenus"
                        class="h-5 w-5 transition-transform"
                      />
                      <ChevronRightIcon
                        v-else
                        class="h-5 w-5 transition-transform"
                      />
                    </span>
                  </button>
                  <div v-show="item.showSubMenus" class="space-y-1">
                    <router-link
                      v-for="(subItem, subIndex) in item.subMenus"
                      :key="subItem.name"
                      :to="`/firm/${firmId}${slash(subItem.name)}${subItem.href}`"
                      @click="changeMenu(index, subIndex)"
                      :class="[
                        subItem.current ? 'bg-custom-purple-500' : 'hover:bg-custom-purple-500',
                        'm-2 group flex items-center pl-14 py-2 text-sm font-semibold rounded-md']"
                    >
                      {{ subItem.name }}
                    </router-link>
                  </div>
                </div>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col w-0 flex-1 overflow-hidden">
      <div class="relative z-30 flex-shrink-0 flex h-16 bg-white">
        <button
          class="
            px-4
            border-r
            text-gray-500
            focus:outline-none
            focus:ring-2 focus:ring-inset focus:ring-indigo-500
            "
          :class="desktopMenuOpen ? 'md:hidden': ''"
          @click="sidebarOpen = true; desktopMenuOpen = true;"
        >
          <span class="sr-only">Open sidebar</span>
          <MenuAlt2Icon class="h-6 w-6" aria-hidden="true" />
        </button>
        <div class="flex-1 px-4 flex justify-between">
          <div class="ml-4 flex items-right md:ml-6">
            <!-- Profile dropdown -->
            <Menu as="div" class="m-3 fixed right-0">
              <div class="">
                <MenuButton
                  class="
                    max-w-xs
                    bg-white
                    flex
                    items-center
                    text-sm
                    rounded-full
                    focus:outline-none
                    focus:text-gray-500
                  "
                >
                  <span class="sr-only">Open user menu</span>
                  <Avatar :user="this.user"/>
                </MenuButton>
              </div>
              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <MenuItems
                  class="
                    origin-top-right
                    absolute
                    right-0
                    mt-2
                    w-48
                    rounded-md
                    shadow-lg
                    py-1
                    bg-white
                    ring-1 ring-black ring-opacity-5
                    focus:outline-none
                  "
                >
                  <MenuItem
                    v-for="item in userNavigation"
                    :key="item.name"
                    v-on:click="profileFunction(item.name)"
                  >
                    <a
                      :href="item.href"
                      :class="[
                        current == item.href ? 'bg-gray-100' : '',
                        'block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900',
                      ]"
                      >{{ item.name }}</a
                    >
                  </MenuItem>
                </MenuItems>
              </transition>
            </Menu>
          </div>
        </div>
      </div>

      <main
        class="
          flex-1
          mt-2
          relative
          overflow-y-auto
          text-left
          focus:outline-none
          bg-white
        "
      >
        <div class="py-2">
          <div class="lg:px-12 px-4 sm:px-6 md:px-8">
            <h1
              v-if="currentRouteName == 'Dashboard' && firstName"
              class="text-2xl font-semibold text-gray-900"
            >
              Hi, {{ firstName }}
            </h1>
            <h1
              v-else-if="currentRouteName == 'Members'"
              class="text-2xl font-semibold text-gray-900"
            >
              Members
            </h1>
            <h1
              v-else-if="currentRouteName == 'Clients'"
              class="text-2xl font-semibold text-gray-900"
            >
              Clients
            </h1>
            <h1
              v-else-if="currentRouteName == 'Patent Applications'"
              class="text-2xl font-semibold text-gray-900"
            >
              Patent Applications
            </h1>
            <h1
              v-else-if="currentRouteName == 'Docketing'"
              class="text-2xl font-semibold text-gray-900"
            >
              Docketing Tasks
            </h1>
            <h1
              v-else-if="currentRouteName == 'Office Actions'"
              class="text-2xl font-semibold text-gray-900"
            >
              Office Communications
            </h1>
            <h1
              v-else-if="currentRouteName == 'Trademarks'"
              class="text-2xl font-semibold text-gray-900"
            >
              Trademark Applications
            </h1>
            <h1
              v-else-if="currentRouteName == 'Trademark Docketing'"
              class="text-2xl font-semibold text-gray-900"
            >
              Trademark Docketing
            </h1>
            <h1
              v-else-if="currentRouteName == 'Emails'"
              class="text-2xl font-semibold text-gray-900"
            >
              <div>Emails</div>
              <span class="text-lg font-bold text-custom-purple-900 mb-4">Sync with Gmail or Outlook, organize threads
                by case, create follow-up reminders, and stay on top of all communications</span>
            </h1>
            <h1
              v-else-if="currentRouteName == 'Firm Settings'"
              class="text-2xl font-semibold text-gray-900"
            >
              Firm Settings
            </h1>
            <h1 v-else-if="showTitle" class="text-2xl h-6 font-semibold text-gray-900">
              {{ currentRouteName }}
            </h1>
          </div>
          <ProfileView
            v-if="user"
            :user="user"
            :isOpen="profilePopup"
            v-on:close="closeProfile"
            v-on:saved="user = {...user, ...$event.data}"
          />
          <ChangePassword
            v-if="user"
            :isOpen="passwordPopup"
            v-on:close="passwordPopup = false"
          />
          <AddPassword
            v-if="user"
            :isOpen="addPasswordPopup"
            v-on:close="addPasswordPopup = false"
          />
          <ManageMFA
            v-if="user"
            :isOpen="mfaPopup"
            v-on:close="mfaPopup = false"
          />
          <div class="lg:px-12 px-4 sm:px-6 md:px-8">
            <!-- Replace with your content -->
            <div class="py-6">
              <router-view></router-view>
            </div>
            <!-- /End replace -->
          </div>
        </div>
        <!-- INSERT PROFILE POPUP -->
      </main>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { logout } from "@/services/user.js";
import {
  Dialog,
  DialogOverlay,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import {
  BellIcon,
  CalendarIcon,
  CogIcon,
  FolderIcon,
  OfficeBuildingIcon,
  HomeIcon,
  InboxIcon,
  MenuAlt2Icon,
  UsersIcon,
  XIcon,
  QuestionMarkCircleIcon,
  BadgeCheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  MailIcon,
} from "@heroicons/vue/outline";
import { capitalize } from "lodash";
import Avatar from "@/components/general/Avatar.vue";
import ProfileView from "@/components/user-profile/ProfileView.vue";
import { getUser } from "@/services/user";
import { getFirm } from "@/services/firm";
import { getMember } from "@/services/teams";
import ChangePassword from "@/components/user-profile/ChangePassword.vue";
import EmailProcessor from "@/components/email/EmailProcessor";
import { useEmailSettingsStore } from "@/stores/email_settings_store";
import Spinner from "@/components/general/Spinner.vue";
import { mapStores } from 'pinia';
import ManageMFA from "@/components/user-profile/ManageMFA.vue";
import AddPassword from "@/components/user-profile/AddPassword.vue";
import { usePatentStore } from "@/stores/patent_store";
import { useTrademarkStore } from "@/stores/trademark_store";

const userNavigation = [
  { name: "Your Profile", href: "#" },
  { name: "Change/Add Password", href: "#" },
  { name: "Manage 2nd-Factor Login", href: "#" },
  { name: "Sign Out", href: "#" },
];

export default {
  components: {
    ChangePassword,
    AddPassword,
    ManageMFA,
    Dialog,
    DialogOverlay,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    TransitionChild,
    TransitionRoot,
    BellIcon,
    MenuAlt2Icon,
    XIcon,
    ProfileView,
    Avatar,
    BadgeCheckIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    ChevronLeftIcon,
    MailIcon,
    EmailProcessor,
    Spinner,
  },
  async mounted() {
    console.log("dashboardlayout.vue called ...");
    this.firmId = this.$route.path.split("firm/").pop().split("/")[0];
    const firm = await getFirm(this.firmId).catch(() => { this.notFound = true; });
    this.firmName = firm.name;
    this.uid = this.$cookies.get("uid");
    this.user = await getUser(this.uid);
    this.role = (await getMember(this.firmId, this.uid)).role;
    this.windowHeight = window.innerHeight;
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  computed: {
    current() {
      console.log("current route: ", this.$route.href);
      // if route is empty, return empty string
      if (this.$route.href === "/") {
        return "";
      }
      // if route is firms/{firmId}, return empty string
      if (this.$route.href === `/firm/${this.firmId}`) {
        return "";
      }
      return this.$route.href.split("/").pop();
    },
    currentRouteName() {
      return this.$route.name;
    },
    showTitle() {
      return !(this.$route.name);
    },
    firstName() {
      if (!this.user) {
        return "";
      }
      return capitalize(this.user.firstName);
    },
    emailProcessingStatus() {
      return this.emailSettingsStore.processing_status;
    },
    ...mapStores(useEmailSettingsStore, usePatentStore, useTrademarkStore),
  },
  data: () => {
    return {
      uid: null,
      user: null,
      profilePopup: false,
      passwordPopup: false,
      addPasswordPopup: false,
      firmId: null,
      firmName: "",
      role: "",
      key: 0,
      notFound: false,
      mfaPopup: false,
      navigation: [
        {
          name: "Dashboard",
          href: ``,
          icon: HomeIcon,
          current: false,
        },
        {
          name: "Members",
          href: "members",
          icon: UsersIcon,
          current: false,
        },
        {
          name: "Clients",
          href: "clients",
          icon: OfficeBuildingIcon,
          current: false,

        },
        {
          name: "Patents",
          href: "None",
          icon: FolderIcon,
          current: false,
          showSubMenus: false,
          subMenus: [
            {
              name: "Applications",
              href: "patents",
              icon: FolderIcon,
              current: false,
            },
            {
              name: "Docketing",
              dashboardName: "Your Docketing Tasks",
              href: "docketing",
              icon: CalendarIcon,
              current: false,
            },
            {
              name: "Office Actions",
              href: "office-actions",
              icon: InboxIcon,
              current: false,
            },
          ]
        },
        {
          name: "Trademarks",
          href: "None",
          icon: BadgeCheckIcon,
          current: false,
          showSubMenus: false,
          subMenus: [
            {
              name: "Applications",
              href: "trademarks",
              icon: BadgeCheckIcon,
              current: false,
            },
            {
              name: "Docketing",
              dashboardName: "Your Docketing Tasks",
              href: "trademark-docketing",
              icon: CalendarIcon,
              current: false,
            }
          ]
        },
        {
          name: "Emails",
          href: "emails",
          icon: MailIcon,
          current: false,
        },
        {
          name: "Settings",
          href: "settings",
          icon: CogIcon,
          current: false,
        },
        {
          name: "Help",
          href: "help",
          icon: QuestionMarkCircleIcon,
          current: false,
        },
      ],
      windowHeight: 0,
    };
  },
  setup() {
    const sidebarOpen = ref(false);
    const desktopMenuOpen = ref(true);
    return {
      userNavigation,
      sidebarOpen,
      desktopMenuOpen,
    };
  },
  methods: {
    async profileFunction(data) {
      if (data == userNavigation[0].name) {
        this.profilePopup = true;
      } else if (data == userNavigation[1].name) {
        if (this.user && this.user.needPassword)
          this.addPasswordPopup = true;
        else
          this.passwordPopup = true;
      } else if (data == userNavigation[2].name) {
        this.mfaPopup = true;
      } else if (data == userNavigation[3].name) {
        this.signOut();
      }
    },
    closeProfile() {
      this.profilePopup = false;
      this.key++;
    },
    async signOut() {
      // before loutout, call unsubscribers from stores
      if (this.patentStore.unsubscribePatents && typeof this.patentStore.unsubscribePatents === 'function') {
        this.patentStore.unsubscribePatents();
      }
      if (this.trademarkStore.unsubscribeTm && typeof this.trademarkStore.unsubscribeTm === 'function') {
        this.trademarkStore.unsubscribeTm();
      }
      if (this.trademarkStore.unsubscribeDocketing && typeof this.trademarkStore.unsubscribeDocketing === 'function') {
        this.trademarkStore.unsubscribeDocketing();
      }
      if (this.emailSettingsStore.settingsUnsubscribe && typeof this.emailSettingsStore.settingsUnsubscribe === 'function') {
        this.emailSettingsStore.settingsUnsubscribe();
      }
      await logout();
      this.$cookies.keys().forEach(cookie => {
        this.$cookies.remove(cookie);
      });
      this.$router.push("/signin");
    },
    slash(name) {
      if (name == "Dashboard") {
        return "";
      }
      return "/";
    },
    changeMenu(index, subIndex = null) {
      this.navigation.forEach((item, i) => {
        item.current = i === index;
        if (item.current) {
          if (item.subMenus) {
            if (subIndex !== null) {
              item.subMenus.forEach((subItem, j) => {
                subItem.current = j === subIndex;
              });
            }
            else
            item.showSubMenus = !item.showSubMenus;
          }
        } else {
          if (item.subMenus) {
            item.showSubMenus = false;
            item.subMenus.forEach(subItem => {
              subItem.current = false;
            });
          }
        }
      });
      
    },
    handleResize() {
      this.windowHeight = window.innerHeight;
    }
  },
};
</script>
